/* eslint-disable no-param-reassign */
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { authApi } from './apis/auth';
import { getAccessToken, goToLoginPageWithCurrentUriRedirect } from './auth';
import { APP_ENV, googleMapKey } from './env';
import { emitServerErrorToast, getUnsplashAccessKey } from './utils';

// skipGlobalErrorToast 옵션을 위한 타입 확장
declare module 'axios' {
  interface AxiosRequestConfig {
    skipGlobalErrorToast?: boolean;
    signal?: AbortSignal;
  }
}

export const CORE_API_BASE_URL = {
  production: 'https://api.realworld.to',
  development: 'https://api-test.realworld.to',
  staging: 'https://api-staging-plan.realworld.to'
}[APP_ENV];

const STUDIO_API_BASE_URL = {
  production: 'https://api.studio.realworld.to',
  development: 'https://api-test.studio.realworld.to',
  staging: 'https://api-staging-plan.studio.realworld.to'
}[APP_ENV];

const isRefreshApi = (config: AxiosRequestConfig) =>
  config.baseURL === CORE_API_BASE_URL && config.url === '/api/auth/refresh';

function isNotRequireAccessToken(config: AxiosRequestConfig) {
  const isVerifyEmailApi =
    config.baseURL === STUDIO_API_BASE_URL && config.url === '/auth/verifyEmail';
  const isChangeEmailVerifyApi =
    config.baseURL === STUDIO_API_BASE_URL &&
    !!config.url &&
    /^\/api\/apps\/.+\/changeEmailVerify$/.test(config.url);

  const isPlansInquiriesApi =
    config.baseURL === CORE_API_BASE_URL && config.url === '/api/plans/inquiries';

  return isRefreshApi(config) || isVerifyEmailApi || isChangeEmailVerifyApi || isPlansInquiriesApi;
}

const interceptorRequest = async (config: AxiosRequestConfig) => {
  if (config.baseURL === STUDIO_API_BASE_URL && config.url === '/auth/loginCallback') {
    return config;
  }

  if (isNotRequireAccessToken(config)) return config;

  const accessToken = await getAccessToken();

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  } else {
    await logout();
    goToLoginPageWithCurrentUriRedirect();
  }

  return config;
};

const interceptorResponse = async (error: AxiosError) => {
  if (error.response?.status === 401 && isRefreshApi(error.config)) {
    await logout();
    goToLoginPageWithCurrentUriRedirect();
  } else {
    // skipGlobalErrorToast 옵션이 설정되어 있지 않은 경우에만 전역 토스트 표시
    const skipGlobalErrorToast = error.config?.skipGlobalErrorToast;
    if (!skipGlobalErrorToast) {
      emitServerErrorToast(error);
    }
  }

  return Promise.reject(error);
};

const ADMIN_API_BASE_URL = {
  production: 'https://api.admin.realworld.to',
  development: 'https://api-test.admin.realworld.to',
  staging: 'https://api-staging-plan.admin.realworld.to'
}[APP_ENV];

export const coreApi = axios.create({
  baseURL: CORE_API_BASE_URL
});

export const adminApi = axios.create({
  baseURL: ADMIN_API_BASE_URL
});

export const studioApi = axios.create({
  baseURL: STUDIO_API_BASE_URL
});

coreApi.interceptors.request.use(interceptorRequest);
coreApi.interceptors.response.use((value) => value, interceptorResponse);

studioApi.interceptors.request.use(interceptorRequest);
studioApi.interceptors.response.use((value) => value, interceptorResponse);

adminApi.interceptors.request.use(interceptorRequest);
adminApi.interceptors.response.use((value) => value, interceptorResponse);

export const unsplashApi = axios.create({
  baseURL: 'https://api.unsplash.com',
  headers: {
    Authorization: `Client-ID ${getUnsplashAccessKey()}`
  }
});

export const googleGeocodingApi = axios.create({
  baseURL: 'https://maps.googleapis.com/maps/api/geocode/json',
  params: { key: googleMapKey }
});

export async function logout() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('defaultChannelID');
  localStorage.removeItem('expiresAt');
  try {
    await authApi.apiAuthLogoutPost();
  } catch (error) {
    console.error('Auth서버 로그아웃 실패', error);
  }
}
